import { useDeferredValue, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { reservations } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../data/use-api-suspense-query';
import { QueryBoundary } from '../layouts/query-boundary';
import { TableSkeleton } from '../layouts/skeletons';
import { useCurrentUser } from '../plugs/current-user';
import { reservationListRequest } from './api/queries';
import type { ReservationListItem } from './components/reservation-list-view';
import { useQueueByReservationId } from './hooks/use-reservation-queue-panel';
import { useReservationPermissions } from './hooks/use-reservation-permissions';
import type { ReservationPermissions } from './hooks/use-reservation-permissions';
import { useSalesDefaultScope } from './hooks/use-sales-default-scope';
import {
  buildReservationListQueryParams,
  makeReservationListItemMapper,
} from './lib/reservations-list-search';
import type {
  ReservationFilterOptions,
  ReservationsSearch,
  ReservationsSearchChange,
} from './lib/reservations-list-search';
import { ReservationsListBody } from './reservations-list-body';

export interface ReservationsListPageProps {
  search: ReservationsSearch;
  onSearchChange: ReservationsSearchChange;
  filterOptions: ReservationFilterOptions;
}

interface ReservationsListContentProps extends ReservationsListPageProps {
  permissions: ReservationPermissions;
}

const NO_QUEUE: Record<number, never[]> = {};
const NO_UNIT_IDS = new Map<number, number>();

interface QueuedBodyProps extends ReservationsListContentProps {
  rows: ReservationListItem[];
  total: number;
}

function QueuedReservationsBody(props: QueuedBodyProps) {
  const { queueByReservationId, mediaUnitIdByReservationId } = useQueueByReservationId(props.rows);
  return (
    <ReservationsListBody
      {...props}
      queueByReservationId={queueByReservationId}
      mediaUnitIdByReservationId={mediaUnitIdByReservationId}
    />
  );
}

function ReservationsListContent(props: ReservationsListContentProps) {
  const { t } = useTranslation('reservations');
  // The list is requested with the deferred search so a filter or page change keeps the table on screen.
  const requested = useDeferredValue(props.search);
  const { data } = useApiSuspenseQuery(
    reservations.list,
    reservationListRequest(buildReservationListQueryParams(requested)),
  );
  const mapItem = useMemo(
    () =>
      makeReservationListItemMapper(t as (key: string, opts?: Record<string, unknown>) => string),
    [t],
  );
  const rows = useMemo(() => data.rows.map(mapItem), [data, mapItem]);

  // The batched queue endpoint needs at least one reservation id.
  if (rows.length === 0) {
    return (
      <ReservationsListBody
        {...props}
        rows={rows}
        total={data.total}
        queueByReservationId={NO_QUEUE}
        mediaUnitIdByReservationId={NO_UNIT_IDS}
      />
    );
  }
  return <QueuedReservationsBody {...props} rows={rows} total={data.total} />;
}

export function ReservationsListPage(props: ReservationsListPageProps) {
  const permissions = useReservationPermissions();
  const { user } = useCurrentUser();
  useSalesDefaultScope(props.search, user?.id, permissions, props.onSearchChange);

  return (
    <QueryBoundary
      fallback={
        <div className="px-6 pt-4">
          <TableSkeleton />
        </div>
      }
    >
      <ReservationsListContent {...props} permissions={permissions} />
    </QueryBoundary>
  );
}
